// WithClipMessage.jsx
import { useCallback, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';

import trackMixpanel, {
  EventTypes,
  ButtonId,
  getViewId,
} from '../resource/mixpanel.js';
import { PlayerContext } from '../resource/contexts.js';

import ClipIconResource from '../../img/ic_clip.svg';

export const WithClipMessage = ({
  messageId,
  duration,
  postVideoDurationMin,
  clipDurationMin,
  children,
  toggleModal,
  pushToastr,
}) => {
  const { player } = useContext(PlayerContext);
  const trackUserClick = ({ buttonId }) => {
    trackMixpanel({
      type: EventTypes.USER_CLICKED,
      payload: {
        viewId: getViewId({ pathname: window.location.pathname }),
        buttonId,
      },
    });
  };
  const handleClick = useCallback(() => {
    trackUserClick({
      buttonId: ButtonId.All.ButtonVideoClip,
    });
    if (!player) {
      return pushToastr({
        textKey: 'text_load_video_failed',
        color: 'error',
      });
    }
    player.pause();
    const playerCurrentTime = player.currentTime() || 0;
    const currentTime = Number.parseInt(playerCurrentTime);
    const remainingTime = duration - currentTime;
    const isDurationGreaterThanPostVideoMin = duration >= postVideoDurationMin;
    const isGreaterThanClipDurationMin = remainingTime >= clipDurationMin;
    return toggleModal({
      modalId: 'ClipConfirmModal',
      payload: {
        messageId,
        isClipable:
          isDurationGreaterThanPostVideoMin && isGreaterThanClipDurationMin,
        clipStartMsec: Number.parseInt(playerCurrentTime * 1000),
        clipEndMsec: Number.parseInt(
          (playerCurrentTime + clipDurationMin) * 1000
        ),
      },
      isOpen: true,
    });
  }, [
    clipDurationMin,
    duration,
    messageId,
    player,
    postVideoDurationMin,
    pushToastr,
    toggleModal,
  ]);
  const result = useMemo(() => {
    return children({
      iconSource: ClipIconResource,
      handleClick,
    });
  }, [children, handleClick]);
  return result;
};

WithClipMessage.propTypes = {
  messageId: PropTypes.string,
  duration: PropTypes.number,
  postVideoDurationMin: PropTypes.number,
  clipDurationMin: PropTypes.number,
  children: PropTypes.func,
  toggleModal: PropTypes.func,
  pushToastr: PropTypes.func,
};

WithClipMessage.defaultProps = {
  messageId: null,
  duration: 0,
  postVideoDurationMin: 0,
  clipDurationMin: 0,
  children: () => null,
  toggleModal: () => null,
  pushToastr: () => null,
};

export default WithClipMessage;
